import React, { useState } from "react";
import axios from "axios";
import { CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { SERVICES } from "@/data/site";
import { useT } from "@/i18n/LanguageContext";
import { localizeService } from "@/i18n/data.es";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initial = {
  name: "",
  email: "",
  phone: "",
  preferred_time: "",
  service: "",
  message: "",
  consent: true,
};

export default function ContactForm({ defaultService = "" }) {
  const { t, lang } = useT();
  const [form, setForm] = useState({ ...initial, service: defaultService });
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState("");

  const setField = (k) => (e) =>
    setForm((f) => ({
      ...f,
      [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
    }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setErrorMsg("");
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        preferred_time: form.preferred_time || null,
        service: form.service || null,
        message: form.message.trim(),
        consent: form.consent,
      });
      setStatus("success");
      setForm({ ...initial });
    } catch (err) {
      setStatus("error");
      const detail = err?.response?.data?.detail;
      setErrorMsg(
        typeof detail === "string"
          ? detail
          : t("Something went wrong. Please call us at 571-510-3034."),
      );
    }
  };

  if (status === "success") {
    return (
      <div
        data-testid="contact-success"
        className="bg-[var(--surface)] border border-[var(--border)] rounded-3xl p-8 md:p-10 text-center"
      >
        <CheckCircle2 className="w-12 h-12 text-[var(--secondary-accent)] mx-auto" />
        <h3 className="font-serif-display text-3xl mt-4 text-[var(--primary)]">
          {t("Thank you — we received your note.")}
        </h3>
        <p className="text-[var(--text-muted)] mt-3 max-w-md mx-auto">
          {t("A member of our team will reach out within one business day. For urgent care, call us directly at")}{" "}
          <a
            href="tel:571-510-3034"
            className="text-[var(--accent)] underline underline-offset-4"
          >
            571-510-3034
          </a>
          .
        </p>
        <button
          data-testid="contact-send-another"
          onClick={() => setStatus("idle")}
          className="mt-6 inline-flex items-center px-6 py-3 rounded-full border border-[var(--primary)] text-[var(--primary)] hover:bg-[var(--primary)] hover:text-[var(--bg)]"
        >
          {t("Send another message")}
        </button>
      </div>
    );
  }

  return (
    <form
      data-testid="contact-form"
      onSubmit={submit}
      className="bg-[var(--surface)] border border-[var(--border)] rounded-3xl p-6 md:p-10"
      noValidate
    >
      <div className="grid md:grid-cols-2 gap-5">
        <Field label={t("Your name")} required>
          <input
            data-testid="contact-name"
            required
            type="text"
            value={form.name}
            onChange={setField("name")}
            placeholder="Jane Smith"
            className={inputCls}
          />
        </Field>
        <Field label={t("Email")} required>
          <input
            data-testid="contact-email"
            required
            type="email"
            value={form.email}
            onChange={setField("email")}
            placeholder="jane@email.com"
            className={inputCls}
          />
        </Field>
        <Field label={t("Phone")} required>
          <input
            data-testid="contact-phone"
            required
            type="tel"
            value={form.phone}
            onChange={setField("phone")}
            placeholder="(571) 555-0123"
            className={inputCls}
          />
        </Field>
        <Field label={t("Preferred time")}>
          <select
            data-testid="contact-time"
            value={form.preferred_time}
            onChange={setField("preferred_time")}
            className={inputCls}
          >
            <option value="">{t("Any time")}</option>
            <option>{t("Weekday mornings")}</option>
            <option>{t("Weekday afternoons")}</option>
            <option>{t("Evenings")}</option>
            <option>{t("Weekends")}</option>
          </select>
        </Field>
        <Field label={t("Service of interest")} className="md:col-span-2">
          <select
            data-testid="contact-service"
            value={form.service}
            onChange={setField("service")}
            className={inputCls}
          >
            <option value="">{t("I'm not sure — please advise")}</option>
            {SERVICES.map((s) => {
              const ls = localizeService(s, lang);
              return (
                <option key={s.slug} value={s.title}>
                  {ls.title}
                </option>
              );
            })}
            <option>{t("New patient cleaning & exam ($129)")}</option>
            <option>{t("BOTOX® / JUVÉDERM®")}</option>
            <option>{t("Other")}</option>
          </select>
        </Field>
        <Field label={t("How can we help?")} required className="md:col-span-2">
          <textarea
            data-testid="contact-message"
            required
            value={form.message}
            onChange={setField("message")}
            placeholder={t("Tell us a little about what you're looking for…")}
            rows={5}
            className={`${inputCls} resize-y`}
          />
        </Field>
      </div>

      <label className="mt-5 flex items-start gap-3 text-sm text-[var(--text-muted)] cursor-pointer">
        <input
          data-testid="contact-consent"
          type="checkbox"
          checked={form.consent}
          onChange={setField("consent")}
          className="mt-0.5 accent-[var(--accent)]"
        />
        <span>
          {t("I consent to being contacted by Smiles of Loudoun about my inquiry. We never share your information.")}
        </span>
      </label>

      {status === "error" && (
        <div
          data-testid="contact-error"
          className="mt-5 flex items-start gap-2 text-sm bg-red-50 border border-red-200 text-red-700 rounded-2xl p-4"
        >
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{errorMsg}</span>
        </div>
      )}

      <button
        data-testid="contact-submit"
        type="submit"
        disabled={status === "loading"}
        className="mt-7 inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[var(--accent)] text-white hover:bg-[var(--accent-hover)] disabled:opacity-70 disabled:cursor-not-allowed"
      >
        {status === "loading" ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" /> {t("Sending…")}
          </>
        ) : (
          <>{t("Send Message")}</>
        )}
      </button>
    </form>
  );
}

const inputCls =
  "w-full px-4 py-3.5 bg-[var(--bg)] border border-[var(--border)] rounded-2xl text-[var(--primary)] placeholder:text-[var(--text-muted)]/70 focus:outline-none focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--accent)]/20 transition";

function Field({ label, required, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-xs uppercase tracking-[0.2em] text-[var(--text-muted)] mb-2">
        {label}
        {required && <span className="text-[var(--accent)]"> *</span>}
      </span>
      {children}
    </label>
  );
}
